import { NO_LIST_FILTERS, sameListFilters } from '../../../creatives/game-filters';
import type { GameSourceRecord } from '../../../creatives/game-sources';
import type { OrderedSourceSnapshot } from '../../../creatives/ordered-source';
import type { OwnerGameList } from '../../../creatives/owner-game-lists-api';
import { ownListChanged, type OwnListState } from '../../../creatives/own-lists';
import { arrangementWithOwnLists } from '../../../creatives/own-lists-gate';
import type { PageArrangement, PageSectionsState } from '../../../creatives/page-arrangement-api';
import { pageListRows, resolveArrangement, sameArrangement, type PageOwnList } from '../../../creatives/page-lists';
import { isOfferedGameList, sameOrder } from '../../../creatives/site-games';
import { unpublishedLists } from '../../../creatives/unpublished-lists';

/**
 * Whether the Homepage has anything to publish, by the Games tab's own rules, from what the card can read without
 * the live site: the page's arrangement, the owner's own lists and each platform list's saved state. Pure.
 *
 * The tab compares a platform list's draft with the order the framed site shows; the card has no frame, so it
 * compares with the service's `live` order, and a saved draft against an empty one counts as a change (the accepted
 * difference, see the spec). The game catalogue is needed for one case only: an automatic list that keeps its mode and
 * filters while its saved order differs from the published one, where only the resolved games can tell whether the
 * order the site shows changes. Without it that case is `needs-catalogue`, and only when nothing else is a change.
 *
 * A Homepage the owner cannot arrange has no arrangement to read (the service refuses it), and the platform's lists on
 * it are named by its arrangement or by the live site alone: there, only the owner's own lists are checked (the tab's
 * change bar counts their games on any page), a platform list's saved draft is not seen, and nothing is counted.
 */

export type GamesCheckInput = {
  /** The `owner-game-lists` flag. Off, the owner's lists are neither read nor go by: the page is left as it is. */
  ownListsOn: boolean;
  /**
   * The Homepage's arrangement, or null when the owner cannot arrange the Homepage (its source does not allow the
   * brand's override, the Games tab's own gate), whose arrangement the service refuses to read.
   */
  sections: PageSectionsState | null;
  /** The owner's own lists, already filtered to the Homepage. */
  ownLists: OwnerGameList[];
  /** What the service holds for each platform list it answered for, by list id. */
  snapshots: Record<string, OrderedSourceSnapshot>;
  catalogue: readonly GameSourceRecord[] | null;
};

type ListCounts = { total: number; hidden: number };

/** The verdict, and the Homepage's lists as counted from its arrangement (null without one). */
export type GamesCheck = { verdict: 'changed' | 'clean' | 'needs-catalogue'; lists: ListCounts | null };

/** The platform's lists on the live page that the Games tab offers (`isOfferedGameList`), once each: the lists whose
 *  saved state the card reads. */
export const platformListIds = (sections: PageSectionsState): string[] => [
  ...new Set(
    pageListRows(resolveArrangement(sections.platform, sections.published))
      .filter((row) => !row.own && isOfferedGameList(row.listId))
      .map((row) => row.listId),
  ),
];

/** The owner's lists as the page places them, as the tab builds them (a list with no source cannot be placed). */
const ownOnPageOf = (lists: OwnerGameList[]): PageOwnList[] =>
  lists
    .filter((list) => list.source_id !== '')
    .map((list) => ({
      sourceId: list.source_id,
      name: list.name,
      names: list.names,
      kind: list.kind,
      layout: list.layout,
    }));

/** A list as the owner is editing it. */
const workingStateOf = (list: OwnerGameList): OwnListState => ({
  mode: list.mode,
  filters: list.filters,
  order: list.game_ids,
});

/** A list as the site shows it, read as the tab reads it: a list published before lists had a mode was hand-picked,
 *  and one the service says nothing published about is one whose every save went to the site. */
const publishedStateOf = (list: OwnerGameList): OwnListState => ({
  mode: list.published_mode ?? (list.published_game_ids ? 'manual' : list.mode),
  filters: list.published_mode ? list.published_filters : list.published_game_ids ? null : list.filters,
  order: list.published_game_ids ?? list.game_ids,
});

/** Whether one of the owner's lists differs from what the site shows, or the catalogue is needed to tell. */
const ownListVerdict = (
  list: OwnerGameList,
  catalogue: readonly GameSourceRecord[] | null,
): 'changed' | 'same' | 'needs-catalogue' => {
  const working = workingStateOf(list);
  const published = publishedStateOf(list);
  if (working.mode !== published.mode) return 'changed';
  if (working.mode === 'manual') return sameOrder([...working.order], [...published.order]) ? 'same' : 'changed';
  if (!sameListFilters(working.filters ?? NO_LIST_FILTERS, published.filters ?? NO_LIST_FILTERS)) return 'changed';
  if (sameOrder([...working.order], [...published.order])) return 'same';
  if (catalogue === null) return 'needs-catalogue';
  return ownListChanged(working, published, catalogue) ? 'changed' : 'same';
};

/** The lists on a page the Games tab offers, each once, and those hidden everywhere the page would show them: the
 *  owner's own lists always, the platform's only when `isOfferedGameList` (as for the lists whose state is read). */
const listCounts = (page: PageArrangement): ListCounts => {
  const rows = pageListRows(page).filter((row) => row.own || isOfferedGameList(row.listId));
  const all = new Set(rows.map((row) => row.listId));
  const shown = new Set(rows.filter((row) => !row.hidden).map((row) => row.listId));
  return { total: all.size, hidden: [...all].filter((listId) => !shown.has(listId)).length };
};

/**
 * The Homepage's verdict and its counts. It stops at the first change it finds: the arrangement (a move, a Hide, one
 * of the owner's lists not on the live page yet, or changed in name or shape), then the owner's lists, then the
 * platform's lists' saved drafts. Without an arrangement, the owner's lists alone.
 */
export const gamesCheck = (input: GamesCheckInput): GamesCheck => {
  const { ownListsOn, sections, snapshots, catalogue } = input;
  const ownLists = ownListsOn ? input.ownLists : [];
  let lists: ListCounts | null = null;
  if (sections) {
    const livePage = resolveArrangement(sections.platform, sections.published);
    const workingPage = arrangementWithOwnLists(
      ownListsOn,
      resolveArrangement(sections.platform, sections.draft.order.length > 0 ? sections.draft : sections.published),
      ownOnPageOf(ownLists),
    );
    lists = listCounts(workingPage);
    if (!sameArrangement(workingPage, livePage)) return { verdict: 'changed', lists };
  }

  let needsCatalogue = false;
  for (const list of ownLists) {
    const verdict = ownListVerdict(list, catalogue);
    if (verdict === 'changed') return { verdict: 'changed', lists };
    if (verdict === 'needs-catalogue') needsCatalogue = true;
  }

  const platformWaiting = unpublishedLists({
    listIds: sections ? platformListIds(sections) : [],
    drafts: {},
    siteOrders: {},
    snapshots,
  });
  if (platformWaiting.size > 0) return { verdict: 'changed', lists };

  return { verdict: needsCatalogue ? 'needs-catalogue' : 'clean', lists };
};
